<script>
import PerPageSelect from "@/components/ui/PerPageSelect.vue";

export default {
    components: {PerPageSelect},
    props: ['companyId'],
    data() {
        return {
            data: [],
            loading: true,
            loaded: false,
            meta: {
                current_page: 1,
                total: 0,
                per_page: 0,
            },
            sort: {
                field: 'id',
                order: 'desc'
            },
            controller: null,
            statusOptions: ['pending', 'transferred', 'settled'],
            originalData: {},
            perPage: parseInt(localStorage.getItem('perPage_withdrawalSettlements') || '10'),
        }
    },
    methods: {
        loadData() {
            this.loading = true;
            this.controller = new AbortController();
            axios.get('/companies/' + this.companyId + '/withdrawal-settlements', {
                params: {
                    page: this.meta.current_page,
                    per_page: this.perPage,
                },
                signal: this.controller.signal
            }).then(response => {
                this.data = response.data.data;
                this.meta.current_page = response.data.meta.current_page;
                this.meta.total = response.data.meta.total;
                this.meta.per_page = response.data.meta.per_page;
            })
            .catch(error => {})
            .finally(() => {
                this.loaded = true;
                this.loading = false;
            })
        },
        onPageChange(page) {
            document.getElementById('withdrawals-settlements-table').scrollIntoView();
            this.controller.abort();
            this.meta.current_page = page;
            this.loadData();
        },
        onSort(field, order) {
            this.controller.abort();
            this.sort = {field, order};
            this.loadData();
        },
        onPerPageChange() {
            localStorage.setItem('perPage_withdrawalSettlements', String(this.perPage));
            this.meta.current_page = 1;
            this.loadData();
        },
        edit(row) {
            this.originalData[row.id] = {...row};
            row.editing = true;
        },
        cancelEditing(row) {
            row.status = this.originalData[row.id].status;
            row.status_label = this.originalData[row.id].status_label;
            row.editing = false;
        },
        save(row) {
            row.saving = true;
            axios.put('/companies/withdrawal-settlements/' + row.id, {
                status: row.status,
            }).then(response => {
                this.$toast.success(this.$t('messages.updated_successfully'));
                row.status_label = response.data.data.status_label;
                row.status = response.data.data.status;
            }).catch(error => {
                this.$toast.error(this.$t('messages.update_failed'))
            }).finally(() => {
                row.editing = false;
                row.saving = false;
            })
        },
    },
    beforeUnmount() {
        this.controller ? this.controller.abort() : null;
    },
    mounted() {
        this.loadData();
    }
}
</script>

<template>

    <div class="block has-text-grey has-text-centered mb-4 mt-4" v-if="!loading && !data.length">
        {{ $t('withdrawal_settlements.labels.no_settlements') }}
    </div>
    <div id="withdrawals-settlements-table"></div>
    <b-table
        v-if="!loaded || (loaded && data.length > 0)"
        :data="loading ? [] : data"
        :loading="loading"

        :paginated="!loading"
        backend-pagination
        :total="loading ? 0 : meta.total"
        :per-page="perPage"
        @page-change="onPageChange"
        :aria-next-label="$t('navigation.next_page')"
        :aria-previous-label="$t('navigation.previous_page')"
        :aria-page-label="$t('navigation.page')"
        :aria-current-label="$t('navigation.current_page')"

        backend-sorting
        sort-icon="arrow-up"
        default-sort-direction="asc"
        :default-sort="['created_at', 'desc']"
        @sort="onSort">

        <b-table-column field="name" label="ID" width="350" v-slot="props">
            {{ props.row.name }}
        </b-table-column>

        <b-table-column field="amount" :label="$t('withdrawal_settlements.fields.amount.name')" width="250" v-slot="props">
            {{ props.row.amount }}
        </b-table-column>

        <b-table-column field="status" :label="$t('withdrawal_settlements.fields.status.name')" width="415" v-slot="props">
            <span v-if="!props.row.editing">{{ $t('withdrawal_settlements.fields.status.options.' + props.row.status) }}</span>
            <b-select v-if="props.row.editing" v-model="props.row.status" :disabled="props.row.saving">
                <option
                    v-for="option in statusOptions"
                    :value="option"
                    :key="option">
                    {{ $t('withdrawal_settlements.fields.status.options.' + option) }}
                </option>
            </b-select>
        </b-table-column>

        <b-table-column label="-" width="130" v-slot="props">
            <section class="buttons">
                <b-button type="is-link" size="is-small"
                        :title="$t('actions.details')"
                        @click="$router.push({name: 'companies.withdrawal-settlement', params: {id: companyId, settlementId: props.row.id}})"
                        icon-right="eye"
                        v-if="!props.row.editing">
                </b-button>

                <b-button type="is-link" size="is-small"
                        :title="$t('actions.edit')"
                        @click="edit(props.row)"
                        icon-right="square-edit-outline"
                        v-if="!props.row.editing">
                </b-button>

                <b-button type="is-success" size="is-small"
                        :title="$t('actions.save')"
                        :disabled="props.row.saving"
                        @click="save(props.row)"
                        :loading="props.row.saving"
                        icon-right="check"
                        v-if="props.row.editing">
                </b-button>

                <b-button type="" size="is-small"
                        :title="$t('actions.cancel')"
                        :disabled="props.row.saving"
                        :loading="props.row.saving"
                        @click="cancelEditing(props.row)"
                          icon-right="close"
                        v-if="props.row.editing">
                </b-button>
            </section>
        </b-table-column>
        <template #bottom-left>
            <PerPageSelect v-model="perPage" @change="onPerPageChange" />
        </template>

    </b-table>

    <loader :loading="loading"/>
</template>
